import {useMemo} from "react";
import {DataTable, DataTableAction} from "../../list/list/DataTable";
import {PagerModel} from "../../list/list/PagerModel";
import {ColumnType} from "../../list/list/ColumnType";
import {t} from "../../../i18n";
import {BudgetTransactionSourceType, PendingBudgetTransaction} from "../../../http/protocol";
import {TransactionActionType} from "./AdminTransactionConfirmModal";

interface AdminTransactionsTableProps {
  transactions: PendingBudgetTransaction[];
  pager: PagerModel;
  setPager: (newPager: PagerModel) => void;
  handleAction: (item: PendingBudgetTransaction, rejectOrApprove: TransactionActionType) => void;
}

// Column names map to dataTable.header.{camelCase(name)} translation keys
const COLUMNS = [
  {
    property: "adminUserId",
    name: "Admin ID",
    type: ColumnType.LINK,
    getUrl: (item: PendingBudgetTransaction) => `/admin/admin-user/${item.adminUserId}`,
    orderBy: "adminUserId",
  },
  { property: "adminUserEmail", name: "Admin User" },
  { property: "amount", name: "Amount" },
  { property: "amountEur", name: "Amount (EUR)" },
  { property: "currency", name: "Currency" },
  { property: "formattedTimestamp", name: "Timestamp", orderBy: "created" },
  { property: "sourceTransactionType", name: "Type" },
  {
    property: "targetUserId",
    name: "Target User ID",
    type: ColumnType.LINK,
    getUrl: (item: PendingBudgetTransaction) => {
      if (!item.targetUserId) return "";
      return item.sourceTransactionType === BudgetTransactionSourceType.AFFILIATE_PAYOUT
        ? `/affiliate/user/${item.targetUserId}`
        : `/user/${item.targetUserId}`;
    }
  },
];

function AdminTransactionsTable({ transactions, pager, setPager, handleAction }: AdminTransactionsTableProps) {
  const actions: DataTableAction[] = useMemo(
    () => [
      { id: "approve", name: t("adminTransactions.approve.button"), className: "btn btn-success" },
      { id: "reject", name: t("adminTransactions.reject.button"), className: "btn btn-danger" },
    ],
    [],
  );

  const paginatedItems = useMemo(() => {
    const sorted = [...transactions].sort((a, b) => {
      if (!pager.orderBy) return 0;
      const aValue = a[pager.orderBy as keyof PendingBudgetTransaction];
      const bValue = b[pager.orderBy as keyof PendingBudgetTransaction];
      const comparison = aValue && bValue ? (aValue < bValue ? -1 : aValue > bValue ? 1 : 0) : 0;
      return pager.ascending ? comparison : -comparison;
    });
    return sorted
      .slice(pager.offset, pager.offset + pager.limit)
      .map((transaction) => ({ ...transaction, formattedTimestamp: transaction.created ? new Date(transaction.created).toLocaleString() : "" }));
  }, [transactions, pager.orderBy, pager.ascending, pager.offset, pager.limit]);

  const handlePagerChange = (newPager: PagerModel) => {
    setPager(new PagerModel(newPager, transactions.length));
  };

  return (
    <DataTable
      pager={pager}
      columns={COLUMNS}
      items={paginatedItems}
      itemsUpdating={[]}
      actions={actions}
      onAction={(item, actionId) => {
        handleAction(item as PendingBudgetTransaction, actionId as TransactionActionType);
        return undefined;
      }}
      onPagerChange={handlePagerChange}
    />
  );
}

export default AdminTransactionsTable;
